/**
 * Pure decision logic for the new-article toast (NewArticleToast).
 *
 * Kept framework-free (no React, no 'use client') so the count + cooldown rules
 * are unit-testable in isolation — they are the part most worth proving correct,
 * since the old banner shipped a misleading count and could "scream" at the user.
 *
 * The component owns the side effects (polling, state, refs); this module only
 * answers two questions from plain data: how many articles are new since the
 * reader arrived, and whether the toast should surface right now.
 */

/** Calm cooldown after a dismiss: new content won't re-pop for 10 minutes. */
export const NEW_ARTICLE_COOLDOWN_MS = 10 * 60 * 1000;

/** Shape returned by the `/api/latest-article-timestamp` route. */
export interface RecentArticlesPayload {
  /** publishedAt (ms) of the most recent public article; 0 on error/none. */
  latestTimestamp: number;
  /** publishedAt (ms) of recent public articles (newest-first, ~last 20). */
  recent: number[];
}

export interface ToastEvaluation {
  /** Articles published strictly after the reader's arrival baseline. */
  newCount: number;
  /** Whether the toast should be visible after this poll. */
  show: boolean;
}

/**
 * Count articles published strictly AFTER the reader's arrival baseline.
 *
 * "Strictly after" is deliberate: the article that was already the latest when
 * you loaded the page (its timestamp === the baseline) is something you've
 * effectively already seen, so it is not "new".
 */
export function countNewSince(recent: number[], lastSeenTs: number): number {
  return recent.filter((ts) => ts > lastSeenTs).length;
}

/**
 * Decide whether to surface the toast after a poll, and with what count.
 *
 * - API error / no content (`latestTimestamp <= 0`) → never show (no false positive).
 * - Recently dismissed (`now < cooldownUntil`) → stay quiet, but still report the
 *   count so the caller can update a still-mounted toast silently if it wants.
 * - Otherwise show iff at least one article is newer than the arrival baseline.
 */
export function evaluateToast(params: {
  payload: RecentArticlesPayload;
  lastSeenTs: number;
  cooldownUntil: number;
  now: number;
}): ToastEvaluation {
  const { payload, lastSeenTs, cooldownUntil, now } = params;

  if (!payload.latestTimestamp || payload.latestTimestamp <= 0) {
    return { newCount: 0, show: false };
  }

  const newCount = countNewSince(payload.recent, lastSeenTs);
  if (newCount <= 0) return { newCount: 0, show: false };
  if (now < cooldownUntil) return { newCount, show: false };

  return { newCount, show: true };
}

/**
 * Eyebrow label for the toast, capped at "9+" so a burst of content never
 * shows a screaming number. Singular for one (and defensively for zero).
 */
export function newCountLabel(count: number): string {
  if (count <= 1) return 'New story';
  const n = count > 9 ? '9+' : String(count);
  return `${n} new stories`;
}
